import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import { PageHeader, SearchInput, EmptyState } from "../components/admin/AdminUI";
import DateInput from "../components/DateInput";
import { formatDmy, fileDateStamp } from "../utils/dateFormat";

const typeLabel = {
  masterclass: "Masterclass",
  conference: "Conference",
  openclass: "Open Class",
  orientation: "Orientation",
  graduation: "Graduation",
};

const SpecialRegPage = ({ setLocationAdmin }) => {
  const navigate = useNavigate();
  const [registrations, setRegistrations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMe, setErrorMe] = useState(false);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [classTypeFilter, setClassTypeFilter] = useState("all"); // all | Onsite | Online
  const [yearFilter, setYearFilter] = useState("all");
  const [invitationOnly, setInvitationOnly] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const [editRegistration, setEditRegistration] = useState(null);
  const [editType, setEditType] = useState("");
  const [editName, setEditName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [editChurch, setEditChurch] = useState("");
  const [editCity, setEditCity] = useState("");
  const [editMessage, setEditMessage] = useState("");
  const [editDob, setEditDob] = useState("");
  const [editClassType, setEditClassType] = useState("");
  const [editSaving, setEditSaving] = useState(false);

  const getMe = async () => {
    try {
      await axios.get("/meAdmin");
    } catch {
      setErrorMe(true);
    }
  };

  const fetchRegistrations = async (type) => {
    setIsLoading(true);
    try {
      const params = type !== "all" ? { type } : {};
      const res = await axios.get("/specialregistrations", { params });
      setRegistrations(res.data);
    } catch {
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setLocationAdmin({ pathname: "/specialregpage" });
    getMe();
    fetchRegistrations("all");
  }, []);

  useEffect(() => {
    if (errorMe) {
      Swal.fire({
        title: "Session Expired",
        text: "Please login to your account!",
        icon: "warning",
        background: "white",
        color: "black",
        confirmButtonColor: "#fb3104",
        confirmButtonText: "Ok",
      });
      navigate("/loginAdmin");
    }
  }, [errorMe, navigate]);

  const handleFilterChange = (val) => {
    setFilter(val);
    setSelectedIds([]);
    fetchRegistrations(val);
  };

  const formatDate = (dateStr) => formatDmy(dateStr) || "-";

  const normalizedSearch = search.toLowerCase().replace(/\s+/g, "");

  // Tahun-tahun yang tersedia (dari createdAt) untuk opsi filter tahun.
  const availableYears = [
    ...new Set(registrations.map((r) => (r.createdAt ? new Date(r.createdAt).getFullYear() : null)).filter(Boolean)),
  ].sort((a, b) => b - a);

  const filteredRegistrations = registrations.filter((reg) => {
    const searchOk =
      !normalizedSearch ||
      (reg.name || "").toLowerCase().replace(/\s+/g, "").includes(normalizedSearch) ||
      (reg.email || "").toLowerCase().replace(/\s+/g, "").includes(normalizedSearch) ||
      (reg.phoneNumber || "").toLowerCase().replace(/\s+/g, "").includes(normalizedSearch);
    const classTypeOk = classTypeFilter === "all" || (reg.classType || "") === classTypeFilter;
    const yearOk =
      yearFilter === "all" ||
      (reg.createdAt && String(new Date(reg.createdAt).getFullYear()) === String(yearFilter));
    const invitationOk = !invitationOnly || !!reg.isInvitation;
    return searchOk && classTypeOk && yearOk && invitationOk;
  });

  // ── Bulk select helpers ──
  const visibleIds = filteredRegistrations.map((r) => r.id);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));
  const someSelected = visibleIds.some((id) => selectedIds.includes(id));

  const toggleSelectAll = (checked) => {
    setSelectedIds(checked ? visibleIds : []);
  };
  const toggleSelectOne = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    const result = await Swal.fire({
      text: `Delete ${selectedIds.length} selected registration(s)? This cannot be undone.`,
      icon: "warning",
      background: "white",
      color: "black",
      showCancelButton: true,
      confirmButtonColor: "#fb3104",
      cancelButtonColor: "#6c757d",
      confirmButtonText: `Delete ${selectedIds.length}`,
    });
    if (!result.isConfirmed) return;
    setBulkDeleting(true);
    try {
      const res = await axios.post("/specialregistrations/bulk-delete", { ids: selectedIds });
      setSelectedIds([]);
      await fetchRegistrations(filter);
      Swal.fire({ text: res.data.msg || "Deleted.", icon: "success", background: "white", color: "black", confirmButtonColor: "#fb3104" });
    } catch (error) {
      Swal.fire({ text: error.response?.data?.msg || "Failed to delete.", icon: "error", background: "white", color: "black", confirmButtonColor: "#fb3104" });
    } finally {
      setBulkDeleting(false);
    }
  };

  // Export mengikuti SEMUA filter yang sedang aktif (tab tipe + class type +
  // year + search) — yaitu data yang benar-benar tampil di tabel.
  const handleExport = () => {
    const data = filteredRegistrations;
    if (data.length === 0) {
      Swal.fire({ text: "No data to export for the current filters.", icon: "warning", background: "white", color: "black", confirmButtonColor: "#fb3104" });
      return;
    }
    setExportLoading(true);
    try {
      const rows = data.map((reg) => ({
        Type: typeLabel[reg.type] || reg.type,
        Name: reg.name,
        Email: reg.email,
        WhatsApp: reg.phoneNumber,
        Church: reg.localChurch,
        City: reg.city,
        "Date of Birth": formatDmy(reg.dateOfBirth),
        "Class Type": reg.classType || "",
        Invitation: reg.isInvitation ? "Yes" : "No",
        Message: reg.message || "",
        "Date Registered": formatDate(reg.createdAt),
      }));
      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Special Registrations");
      const stamp = [
        filter,
        classTypeFilter !== "all" ? classTypeFilter : null,
        yearFilter !== "all" ? yearFilter : null,
      ].filter(Boolean).join("-");
      const dateStamp = fileDateStamp();
      XLSX.writeFile(workbook, `special-registrations-${stamp}-${dateStamp}.xlsx`);
    } catch (error) {
      Swal.fire({ text: "Failed to export data.", icon: "error", background: "white", color: "black", confirmButtonColor: "#fb3104" });
    } finally {
      setExportLoading(false);
    }
  };

  const openEditModal = (reg) => {
    setEditRegistration(reg);
    setEditType(reg.type || "");
    setEditName(reg.name || "");
    setEditEmail(reg.email || "");
    setEditPhone(reg.phoneNumber || "");
    setEditChurch(reg.localChurch || "");
    setEditCity(reg.city || "");
    setEditMessage(reg.message || "");
    setEditDob(reg.dateOfBirth || "");
    setEditClassType(reg.classType || "");
  };

  const closeEditModal = () => {
    setEditRegistration(null);
  };

  const handleSaveEdit = async () => {
    if (!editType || !editName || !editEmail || !editPhone || !editChurch || !editCity) {
      Swal.fire({ text: "Please fill all required fields.", icon: "warning", background: "white", color: "black", confirmButtonColor: "#fb3104" });
      return;
    }
    setEditSaving(true);
    try {
      await axios.patch(`/specialregistration/${editRegistration.id}`, {
        type: editType,
        name: editName,
        email: editEmail,
        phoneNumber: editPhone,
        localChurch: editChurch,
        city: editCity,
        message: editMessage,
        dateOfBirth: editDob,
        classType: editClassType,
      });
      closeEditModal();
      await fetchRegistrations(filter);
      Swal.fire({ text: "Registration updated!", icon: "success", background: "white", color: "black", confirmButtonColor: "#fb3104" });
    } catch (error) {
      Swal.fire({ text: error.response?.data?.msg || "Failed to update registration.", icon: "error", background: "white", color: "black", confirmButtonColor: "#fb3104" });
    } finally {
      setEditSaving(false);
    }
  };

  const handleDelete = async (reg) => {
    const result = await Swal.fire({
      text: `Delete registration for "${reg.name}"? This cannot be undone.`,
      icon: "warning",
      background: "white",
      color: "black",
      showCancelButton: true,
      confirmButtonColor: "#fb3104",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Delete",
    });
    if (!result.isConfirmed) return;
    try {
      await axios.delete(`/specialregistration/${reg.id}`);
      await fetchRegistrations(filter);
      Swal.fire({ text: "Registration deleted.", icon: "success", background: "white", color: "black", confirmButtonColor: "#fb3104" });
    } catch (error) {
      Swal.fire({ text: error.response?.data?.msg || "Failed to delete registration.", icon: "error", background: "white", color: "black", confirmButtonColor: "#fb3104" });
    }
  };

  return (
    <div className="adm-main">
      <PageHeader
        title="Special Registrations"
        subtitle="Registrations for Masterclass, Conference, Open Class, Orientation, and Graduation."
        actions={
          <button type="button" className="adm-btn adm-btn--secondary" onClick={handleExport} disabled={exportLoading}>
            <i className="bi bi-file-earmark-excel" style={{ color: "#16a34a" }}></i>
            {exportLoading ? "Exporting..." : "Export to Excel"}
          </button>
        }
      />

      {/* Tab tipe event */}
      <div className="adm-tabs">
        {["all", "masterclass", "conference", "openclass", "orientation", "graduation"].map((val) => (
          <button
            key={val}
            type="button"
            className={`adm-tab${filter === val ? " adm-tab--active" : ""}`}
            onClick={() => handleFilterChange(val)}
          >
            {val === "all" ? "All" : typeLabel[val]}
          </button>
        ))}
      </div>

      {/* Pencarian & filter tambahan */}
      <div className="adm-toolbar">
        <SearchInput value={search} onChange={setSearch} placeholder="Search name, email, or phone" />
        <select
          className="form-select"
          style={{ width: "auto", minWidth: "150px" }}
          value={classTypeFilter}
          onChange={(e) => { setClassTypeFilter(e.target.value); setSelectedIds([]); }}
          aria-label="Class type"
        >
          <option value="all">All class types</option>
          <option value="Onsite">Onsite</option>
          <option value="Online">Online</option>
        </select>
        <select
          className="form-select"
          style={{ width: "auto", minWidth: "120px" }}
          value={yearFilter}
          onChange={(e) => { setYearFilter(e.target.value); setSelectedIds([]); }}
          aria-label="Year"
        >
          <option value="all">All years</option>
          {availableYears.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        <label className={`adm-chip${invitationOnly ? " adm-chip--on" : ""}`} style={{ height: "40px" }}>
          <input
            type="checkbox"
            checked={invitationOnly}
            onChange={(e) => { setInvitationOnly(e.target.checked); setSelectedIds([]); }}
          />
          Invitation only
        </label>
      </div>

      {/* Aksi massal */}
      {selectedIds.length > 0 && (
        <div className="adm-bulk-bar">
          <span>
            <strong>{selectedIds.length}</strong> selected
          </span>
          <div className="d-flex gap-2">
            <button type="button" className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => setSelectedIds([])}>
              Clear selection
            </button>
            <button type="button" className="adm-btn adm-btn--danger adm-btn--sm" onClick={handleBulkDelete} disabled={bulkDeleting}>
              <i className="bi bi-trash"></i>
              {bulkDeleting ? "Deleting..." : `Delete selected (${selectedIds.length})`}
            </button>
          </div>
        </div>
      )}

      <div className="adm-card adm-card--flush">
        {isLoading ? (
          <EmptyState icon="bi-hourglass" title="Loading registrations…" />
        ) : filteredRegistrations.length === 0 ? (
          <EmptyState icon="bi-calendar-event" title="No registrations found" text="Try another tab, search, or filter." />
        ) : (
          <div className="adm-table-wrap adm-table-wrap--scroll">
            <table className="adm-table" style={{ minWidth: "1080px" }}>
              <thead>
                <tr>
                  <th style={{ width: "44px" }}>
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={(el) => { if (el) el.indeterminate = someSelected && !allSelected; }}
                      onChange={(e) => toggleSelectAll(e.target.checked)}
                      aria-label="Select all"
                    />
                  </th>
                  <th>Registrant</th>
                  <th>Type</th>
                  <th>Church & City</th>
                  <th>Class Type</th>
                  <th>Date of Birth</th>
                  <th>Message</th>
                  <th>Registered</th>
                  <th className="adm-col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRegistrations.map((reg) => {
                  const selected = selectedIds.includes(reg.id);
                  return (
                    <tr key={reg.id} className={selected ? "adm-row-selected" : ""}>
                      <td>
                        <input type="checkbox" checked={selected} onChange={() => toggleSelectOne(reg.id)} aria-label={`Select ${reg.name}`} />
                      </td>
                      <td>
                        <div className="adm-cell-title">
                          {reg.name}
                          {reg.isInvitation && (
                            <span className="adm-badge adm-badge--purple" style={{ marginLeft: "6px" }}>Invitation</span>
                          )}
                        </div>
                        <div className="adm-cell-meta">{reg.email}</div>
                        <div className="adm-cell-meta">{reg.phoneNumber}</div>
                      </td>
                      <td>
                        <span className="adm-badge adm-badge--primary">{typeLabel[reg.type] || reg.type}</span>
                      </td>
                      <td>
                        <div>{reg.localChurch || "-"}</div>
                        <div className="adm-cell-meta">{reg.city || "-"}</div>
                      </td>
                      <td>{reg.classType || <span className="adm-muted-text">-</span>}</td>
                      <td style={{ whiteSpace: "nowrap" }}>{formatDmy(reg.dateOfBirth) || <span className="adm-muted-text">-</span>}</td>
                      <td style={{ maxWidth: "200px" }}>
                        <div className="text-truncate" title={reg.message || ""} style={{ color: reg.message ? "var(--adm-text-2)" : "var(--adm-muted)" }}>
                          {reg.message || "-"}
                        </div>
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>{formatDate(reg.createdAt)}</td>
                      <td className="adm-col-actions">
                        <div className="adm-actions">
                          <button type="button" className="adm-icon-btn" title="Edit" aria-label="Edit registration" onClick={() => openEditModal(reg)}>
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button
                            type="button"
                            className="adm-icon-btn adm-icon-btn--danger"
                            title="Delete"
                            aria-label="Delete registration"
                            onClick={() => handleDelete(reg)}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="adm-table-footer">
          <span>
            <strong style={{ color: "var(--adm-text)" }}>{filteredRegistrations.length}</strong> registration
            {filteredRegistrations.length === 1 ? "" : "s"}
          </span>
        </div>
      </div>

      {editRegistration &&
        createPortal(
          <div className="adm-modal-backdrop">
            <div className="adm-modal" style={{ width: "520px" }}>
              <div className="adm-modal-header">
                <h5 className="adm-card-title" style={{ fontSize: "17px" }}>Edit Registration</h5>
                <button type="button" className="adm-modal-close" aria-label="Close" onClick={closeEditModal} disabled={editSaving}>
                  ×
                </button>
              </div>

              <div className="adm-modal-body">
                <div className="adm-form-grid">
                  <div className="adm-span-all">
                    <label className="form-label">
                      Type <span style={{ color: "var(--adm-primary)" }}>*</span>
                    </label>
                    <select className="form-select" value={editType} onChange={(e) => setEditType(e.target.value)}>
                      <option value="masterclass">Masterclass</option>
                      <option value="conference">Conference</option>
                      <option value="openclass">Open Class</option>
                      <option value="orientation">Orientation</option>
                      <option value="graduation">Graduation</option>
                    </select>
                  </div>
                  <div className="adm-span-all">
                    <label className="form-label">
                      Name <span style={{ color: "var(--adm-primary)" }}>*</span>
                    </label>
                    <input type="text" className="form-control" value={editName} onChange={(e) => setEditName(e.target.value)} />
                  </div>
                  <div>
                    <label className="form-label">
                      Email <span style={{ color: "var(--adm-primary)" }}>*</span>
                    </label>
                    <input type="email" className="form-control" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} />
                  </div>
                  <div>
                    <label className="form-label">
                      WhatsApp Number <span style={{ color: "var(--adm-primary)" }}>*</span>
                    </label>
                    <input type="text" className="form-control" value={editPhone} onChange={(e) => setEditPhone(e.target.value.replace(/\D/g, ""))} />
                  </div>
                  <div>
                    <label className="form-label">
                      Local Church <span style={{ color: "var(--adm-primary)" }}>*</span>
                    </label>
                    <input type="text" className="form-control" value={editChurch} onChange={(e) => setEditChurch(e.target.value)} />
                  </div>
                  <div>
                    <label className="form-label">
                      City <span style={{ color: "var(--adm-primary)" }}>*</span>
                    </label>
                    <input type="text" className="form-control" value={editCity} onChange={(e) => setEditCity(e.target.value)} />
                  </div>
                  {editType === "openclass" && (
                    <>
                      <div>
                        <label className="form-label">Date of Birth</label>
                        <DateInput className="form-control" value={editDob} onChange={(e) => setEditDob(e.target.value)} />
                      </div>
                      <div>
                        <label className="form-label">Class Type</label>
                        <select className="form-select" value={editClassType} onChange={(e) => setEditClassType(e.target.value)}>
                          <option value="">-</option>
                          <option value="Onsite">Onsite</option>
                          <option value="Online">Online</option>
                        </select>
                      </div>
                    </>
                  )}
                  <div className="adm-span-all">
                    <label className="form-label">
                      Message <span style={{ color: "var(--adm-muted)", fontWeight: 400 }}>(optional)</span>
                    </label>
                    <textarea className="form-control" rows={3} value={editMessage} onChange={(e) => setEditMessage(e.target.value)} />
                  </div>
                </div>
              </div>

              <div className="adm-modal-footer">
                <button type="button" className="adm-btn adm-btn--secondary" onClick={closeEditModal} disabled={editSaving}>
                  Cancel
                </button>
                <button type="button" className="adm-btn adm-btn--primary" onClick={handleSaveEdit} disabled={editSaving}>
                  {editSaving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default SpecialRegPage;
